import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Device } from "../../../../../api/devices";
import type { UserListItemOut } from "../../../../../api/auth";
import { useViewport } from "../../../../../hooks/useViewport";
import { useStations, useLanguages } from "../../../../../hooks/useStations";
import { getTranslation } from "../../../../../utils/translations";
import { useUnsavedChangesGuard } from "../../panel/useUnsavedChangesGuard";
import { deviceFormSnapshot } from "../deviceFormSnapshot";
import type { DeviceFormValues } from "../DeviceFormModal";
import { buildDeviceAccounts, freeAccountIds } from "../modal/deviceAccounts";
import PosDocumentsSection from "./PosDocumentsSection";
import PosKitchenSection from "./PosKitchenSection";
import DevicePhoneTabs from "../modal/DevicePhoneTabs";
import DeviceModalShell, { DeviceSummaryItem } from "../modal/DeviceModalShell";
import { PrinterIcon } from "../modal/deviceModalIcons";

interface Props {
  open: boolean;
  // Създаване (device=null) или редакция.
  device: Device | null;
  users: UserListItemOut[];
  devices: Device[];
  // Новият GUID при създаване идва от извикващия (общ с другите типове).
  newGuid: () => string;
  // Извикващият прави мутацията и toast-овете; при грешка модалът остава отворен.
  onSubmit: (values: DeviceFormValues) => Promise<void>;
  onCancel: () => void;
}

type PhoneTab = "documents" | "kitchen";

/**
 * Модалът на POS принтер (създаване и редакция) — две независими секции:
 * „Документи от касата" (акаунтите + статуса) и „Кухненски билети"
 * (станциите + езика). На десктоп секциите са една до друга, на таблет и
 * телефон — една под друга; на телефон модалът е на цял екран.
 */
const PosPrinterModal = ({ open, device, users, devices, newGuid, onSubmit, onCancel }: Props) => {
  const { t, i18n } = useTranslation();
  const { isNarrow } = useViewport();
  const { data: stations = [] } = useStations();
  const { data: languages = [] } = useLanguages();
  const isEdit = device !== null;

  const accounts = useMemo(
    () => buildDeviceAccounts(users, devices, "pos", device?.id ?? null),
    [users, devices, device],
  );
  const freeIds = useMemo(() => freeAccountIds(accounts), [accounts]);
  // Стабилна референция — секциите (memo) не бива да се прерисуват при
  // всяка буква от името.
  const languageCodes = useMemo(() => languages.map((language) => language.code), [languages]);
  // Друг POS принтер със станции — за обяснението до примерния билет.
  const otherPrinter = useMemo(
    () =>
      devices.find(
        (otherDevice) =>
          otherDevice.type === "pos" &&
          otherDevice.id !== device?.id &&
          otherDevice.stationIds.length > 0,
      ) ?? null,
    [devices, device],
  );

  // „Всички акаунти" при отваряне: всички свободни (поне два) вече са избрани.
  const initialAllAccounts =
    isEdit &&
    freeIds.length > 1 &&
    freeIds.every((id) => device.userIds.includes(id));
  // Заварено устройство без акаунти тръгва „Неактивно".
  const initiallyEnabled = !isEdit || device.userIds.length > 0;

  const [name, setName] = useState(device?.name ?? "");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(device?.userIds ?? []);
  const [isAllAccounts, setIsAllAccounts] = useState(initialAllAccounts);
  const [isEnabled, setIsEnabled] = useState(initiallyEnabled);
  const [selectedStationIds, setSelectedStationIds] = useState<number[]>(device?.stationIds ?? []);
  const [printLanguage, setPrintLanguage] = useState<string | null>(device?.printLanguage ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // На телефон секциите са раздели — вижда се едната.
  const [phoneTab, setPhoneTab] = useState<PhoneTab>("documents");

  // Името в примера на билета — отложено, за да не прерисува секцията със
  // станциите при всяка буква (полето за име остава бързо).
  const deferredName = useDeferredValue(name);

  // Какво реално се записва: изключено → без акаунти; „Всички" → всички свободни.
  const effectiveUserIds = !isEnabled ? [] : isAllAccounts ? freeIds : selectedUserIds;

  const { requestClose, markPristine, unsavedDialog } = useUnsavedChangesGuard({
    open,
    snapshot: deviceFormSnapshot({
      name,
      userIds: effectiveUserIds,
      stationIds: selectedStationIds,
      printLanguage,
      paymentMethods: [],
      summarizeReceipt: false,
      nonFiscalCopy: false,
      isEnabled,
    }),
    isSubmitting,
    onClose: onCancel,
  });

  useEffect(() => {
    if (!open) return;
    setName(device?.name ?? "");
    setSelectedUserIds(device?.userIds ?? []);
    setIsAllAccounts(initialAllAccounts);
    setIsEnabled(initiallyEnabled);
    setSelectedStationIds(device?.stationIds ?? []);
    setPrintLanguage(device?.printLanguage ?? null);
    setPhoneTab("documents");
    markPristine();
    // Попълва се само при отваряне — не при всяко ново изчисление отгоре.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device]);

  // Създаването иска акаунт ИЛИ станция; редакцията може да „изпразни" принтера.
  const hasAssignment = effectiveUserIds.length > 0 || selectedStationIds.length > 0;
  const canSave = name.trim().length > 0 && !isSubmitting && (isEdit || hasAssignment);

  const handleSave = async () => {
    if (!canSave) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        guid: device?.guid ?? newGuid(),
        name: name.trim(),
        type: "pos",
        userIds: effectiveUserIds,
        stationIds: selectedStationIds,
        printLanguage,
        summarizeReceipt: false,
        nonFiscalCopy: false,
        paymentMethods: [],
      });
    } catch {
      // Toast-ът за грешка е при извикващия; модалът остава отворен.
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedStationNames = stations
    .filter((station) => selectedStationIds.includes(station.id))
    .map((station) => getTranslation(station.translations, i18n.language, station.slug));
  const documentsSummary = !isEnabled
    ? t("devices.posModal.summaryDocsOff")
    : isAllAccounts
      ? t("devices.posModal.summaryDocsAll", { count: freeIds.length })
      : t("devices.posModal.summaryDocsCount", { count: effectiveUserIds.length });

  return (
    <>
      <DeviceModalShell
        open={open}
        onRequestClose={requestClose}
        icon={<PrinterIcon size={20} />}
        kicker={isEdit ? t("devices.posModal.kickerEdit") : t("devices.posModal.kickerCreate")}
        phoneKicker={t("devices.typePos")}
        name={name}
        onNameChange={setName}
        autoFocusName={!isEdit}
        phoneTabs={
          <DevicePhoneTabs
            activeTab={phoneTab}
            onTabChange={setPhoneTab}
            tabs={[
              {
                id: "documents",
                label: t("devices.posModal.tabDocuments"),
                count: isEnabled ? String(effectiveUserIds.length) : t("devices.posModal.tabOff"),
                countClassName: "bg-brand-tint text-brand-deep-ink",
              },
              {
                id: "kitchen",
                label: t("devices.posModal.tabKitchen"),
                count: String(selectedStationIds.length),
                countClassName: "bg-ok-tint text-ok-deep",
              },
            ]}
          />
        }
        summary={
          <>
            <DeviceSummaryItem
              dotClassName="bg-brand"
              label={t("devices.posModal.summaryDocs")}
              value={documentsSummary}
            />
            <DeviceSummaryItem
              dotClassName="bg-ok"
              label={t("devices.posModal.summaryKitchen")}
              value={selectedStationNames.join(", ") || t("devices.posModal.summaryNoStations")}
              isTruncated
            />
          </>
        }
        saveLabel={isEdit ? t("devices.saveChanges") : t("devices.addDevice")}
        canSave={canSave}
        onSave={handleSave}
      >
        {(!isNarrow || phoneTab === "documents") && (
          <PosDocumentsSection
            accounts={accounts}
            selectedIds={selectedUserIds}
            onSelectedChange={setSelectedUserIds}
            isAllAccounts={isAllAccounts}
            onAllAccountsChange={setIsAllAccounts}
            showStatus={isEdit}
            isEnabled={isEnabled}
            onEnabledChange={setIsEnabled}
            isNarrow={isNarrow}
          />
        )}
        {(!isNarrow || phoneTab === "kitchen") && (
          <PosKitchenSection
            stations={stations}
            selectedStationIds={selectedStationIds}
            onSelectedStationsChange={setSelectedStationIds}
            languageCodes={languageCodes}
            printLanguage={printLanguage}
            onPrintLanguageChange={setPrintLanguage}
            printerName={deferredName}
            otherPrinter={otherPrinter}
            isNarrow={isNarrow}
          />
        )}
      </DeviceModalShell>

      {unsavedDialog}
    </>
  );
};

export default PosPrinterModal;
